/**
 * NipostPayLayout — wallet-app shell for the `/nipost-pay/*` subtree.
 *
 * Distinct chrome from the customer portal: a sidebar listing every NIPOST
 * Pay surface plus a topbar with the wallet balance + back-to-portal link.
 * Auth-gated via `RequireCustomerAuth` at the route level.
 *
 * Layout reuses Tailwind tokens (`nipost-green`, `border`, `muted`) shared
 * with the rest of the customer app — no extra theme is introduced.
 */
import { useMemo, useState, type JSX } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight,
  Banknote,
  ChevronDown,
  CreditCard,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  Phone,
  Receipt,
  ShieldCheck,
  Users,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react';

import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { useCustomerProfile } from '../portal/useCustomerProfile';
import { formatNaira, useCustomerWallet } from './NipostPayUI';

interface NavItem {
  readonly key: string;
  readonly label: string;
  readonly path: string;
  readonly icon: LucideIcon;
}

const NAV: readonly NavItem[] = [
  { key: 'dashboard',     label: 'Dashboard',           path: '/nipost-pay',               icon: LayoutDashboard },
  { key: 'onboarding',    label: 'Wallet Onboarding',   path: '/nipost-pay/onboarding',    icon: ShieldCheck },
  { key: 'kyc',           label: 'KYC Tier',            path: '/nipost-pay/kyc',           icon: ShieldCheck },
  { key: 'cash-in',       label: 'Cash In',             path: '/nipost-pay/cash-in',       icon: Banknote },
  { key: 'cash-out',      label: 'Cash Out',            path: '/nipost-pay/cash-out',      icon: Banknote },
  { key: 'transfer',      label: 'P2P Transfer',        path: '/nipost-pay/transfer',      icon: ArrowLeftRight },
  { key: 'airtime',       label: 'Airtime & Data',      path: '/nipost-pay/airtime',       icon: Phone },
  { key: 'bill-pay',      label: 'Bill Payment',        path: '/nipost-pay/bill-pay',      icon: Receipt },
  { key: 'card',          label: 'Debit Card',          path: '/nipost-pay/card',          icon: CreditCard },
  { key: 'disbursements', label: 'Social Disbursements', path: '/nipost-pay/disbursements', icon: Users },
];

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return 'C';
  }
  if (parts.length === 1) {
    return (parts[0]?.[0] ?? 'C').toUpperCase();
  }
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
}

export function NipostPayLayout(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, claims, logout } = useCustomerAuth();
  const customerId = claims?.upmpUserId ?? '';
  const profile = useCustomerProfile();
  const wallet = useCustomerWallet(customerId, token);

  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);

  const displayName = useMemo(() => {
    if (profile.data?.fullName) return profile.data.fullName;
    if (profile.data?.displayName) return profile.data.displayName;
    return claims?.displayName ?? 'Customer';
  }, [profile.data, claims]);

  const initials = initialsFor(displayName);
  const balanceDisplay = wallet.data
    ? formatNaira(wallet.data.balanceCached ?? wallet.data.balance ?? '0')
    : '—';

  function handleSignOut(): void {
    setUserMenuOpen(false);
    logout();
    navigate('/', { replace: true });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Topbar */}
      <header className="sticky top-0 z-30 bg-white border-b border-border shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                type="button"
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
                onClick={() => setMobileOpen((open) => !open)}
                aria-label={mobileOpen ? 'Close sidebar' : 'Open sidebar'}
              >
                {mobileOpen ? <X className="w-5 h-5 text-gray-600" /> : <Menu className="w-5 h-5 text-gray-600" />}
              </button>
              <Link to="/nipost-pay" className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-nipost-green flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 leading-none">NIPOST Pay</p>
                  <p className="text-[10px] text-muted">Wallet portal</p>
                </div>
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex flex-col items-end leading-tight">
                <span className="text-[10px] uppercase tracking-wider text-muted">Balance</span>
                <span
                  className="text-sm font-semibold text-nipost-green"
                  data-testid="nipost-pay-balance"
                >
                  {balanceDisplay}
                </span>
              </div>

              <Link
                to="/portal/dashboard"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border rounded-lg hover:bg-gray-50 text-gray-700"
              >
                <Home className="w-3.5 h-3.5" /> Portal
              </Link>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((open) => !open)}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded-lg"
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                >
                  <div className="w-8 h-8 rounded-full bg-nipost-green flex items-center justify-center">
                    <span className="text-white text-xs font-medium">{initials}</span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted hidden sm:inline" />
                </button>
                {userMenuOpen ? (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserMenuOpen(false)}
                      aria-hidden="true"
                    />
                    <div
                      role="menu"
                      className="absolute right-0 mt-1 w-56 bg-white rounded-xl border border-border shadow-lg z-20 py-1"
                    >
                      <Link
                        to="/portal/dashboard"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        role="menuitem"
                      >
                        <Home className="w-4 h-4" /> Customer portal
                      </Link>
                      <Link
                        to="/portal/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        role="menuitem"
                      >
                        <ShieldCheck className="w-4 h-4" /> Profile
                      </Link>
                      <div className="my-1 border-t border-border" />
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-danger hover:bg-gray-50 w-full text-left"
                        role="menuitem"
                      >
                        <LogOut className="w-4 h-4" /> Sign out
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar + main */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex gap-6">
        <aside
          className={`${mobileOpen ? 'block fixed inset-x-0 top-16 z-20 px-4 py-4 bg-white border-b border-border shadow-md' : 'hidden'} lg:block lg:static lg:w-56 lg:flex-shrink-0 lg:bg-transparent lg:border-0 lg:shadow-none lg:p-0`}
          aria-label="NIPOST Pay navigation"
        >
          <nav className="space-y-1">
            {NAV.map((item) => {
              const Icon = item.icon;
              const isActive =
                item.path === '/nipost-pay'
                  ? location.pathname === '/nipost-pay'
                  : location.pathname === item.path ||
                    location.pathname.startsWith(`${item.path}/`);
              return (
                <NavLink
                  key={item.key}
                  to={item.path}
                  end={item.path === '/nipost-pay'}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-nipost-green-light text-nipost-green'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
